"use client";

import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { GroupedProductsQueryResult } from "@graphql";
import { ErrorMessage } from "@hookform/error-message";
import {
	Checkbox,
	FormErrorMessage,
	NativeInput,
	ProductionUnit,
	RadixSelect,
	RadixSelectItem,
	Typography,
	formatNumber,
} from "@tosize/ui";
import { filter } from "fuzzy";
import { DropdownMenu } from "radix-ui";
import { useDeferredValue, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
	getGroupedProductData,
	getSimpleProductData,
	getSimpleProductsData,
} from "~/app/[lang]/command/dialogs/import/actions";
import type { ListImportFormType } from "~/app/[lang]/command/dialogs/import/schema";

interface ImportDropdownWrapperProps {
	groupedId: string;
	lang: string;
	apiUrl: string;
	messages: {
		description: string;
		grouped: GroupProductDropDownProps["messages"];
		simple: SimpleProductDropDownProps["messages"];
		productionType: ProductionTypeDropDownProps["messages"];
	};
}

export function ImportDropdownWrapper(props: ImportDropdownWrapperProps) {
	const { groupedId, lang, apiUrl, messages } = props;
	const { products, isLoading } = getGroupedProductData({ id: groupedId });

	if (isLoading || !products?.length) {
		return (
			<div className="flex flex-col gap-3">
				<div className="grid grid-cols-6 gap-4 *:col-span-6 xs:*:col-span-3 xs:last:odd:*:col-span-full lg:*:col-span-2 lg:last:odd:*:col-span-2">
					{Array.from({ length: 3 }, (_, index) => (
						<div className="flex flex-col gap-1" key={index}>
							<span className="h-6 w-1/2 animate-pulse rounded bg-shades-light" />
							<span className="h-[2.625rem] w-full animate-pulse rounded bg-shades-light" />
						</div>
					))}
				</div>

				<span className="h-5 animate-pulse rounded bg-shades-light sm:w-1/2" />
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-3">
			<div className="grid grid-cols-6 gap-4 *:col-span-6 xs:*:col-span-3 xs:last:odd:*:col-span-full lg:*:col-span-2 lg:last:odd:*:col-span-2">
				<GroupProductDropDown grouped={products} messages={messages.grouped} />
				<SimpleProductDropDown lang={lang} apiUrl={apiUrl} messages={messages.simple} />
				<ProductionTypeDropDown apiUrl={apiUrl} messages={messages.productionType} />
			</div>

			<Typography color="text-shades-dark" className="text-sm">
				{messages.description}
			</Typography>

			<SimpleProductAgreement apiUrl={apiUrl} />
		</div>
	);
}

interface GroupProductDropDownProps {
	grouped: NonNullable<GroupedProductsQueryResult["data"]>["products"]["items"];
	messages: {
		empty: string;
		placeholder: string;
		search: string;
		woodType: string;
	};
}

function GroupProductDropDown(props: GroupProductDropDownProps) {
	const { grouped, messages } = props;
	const [search, setSearch] = useState("");
	const query = useDeferredValue(search);

	const name = "grouped_product_id";
	const { watch, getFieldState, register, setValue } = useFormContext<ListImportFormType>();
	const groupedProductId = watch(name);
	const hasError = getFieldState(name).invalid;

	const filteredGrouped = useMemo(() => {
		const filtered = filter(query, grouped, {
			pre: "<b>",
			post: "</b>",
			extract: (item) => item.name ?? "",
		});

		return filtered.map((result) => ({
			...(grouped[result.index] as GroupProductDropDownProps["grouped"][number]),
			label: result.string,
		}));
	}, [query, grouped]);

	return (
		<>
			<input type="hidden" {...register(name)} />
			<DropdownMenu.Root onOpenChange={() => setSearch("")}>
				<div className="group flex flex-col gap-1 focus:outline-none">
					<Typography variant="paragraph-bold">{messages.woodType}</Typography>

					<DropdownMenu.Trigger
						disabled={!grouped?.length || grouped.length === 1}
						className="flex items-center justify-between rounded border border-shades p-2 text-left hover:border-primary-medium focus:outline-none focus-visible:border-primary-medium focus-visible:ring-1 focus-visible:ring-primary-medium focus-visible:ring-inset disabled:bg-shades-light motion-safe:transition-colors"
					>
						<span>
							{grouped.find((item) => item.id === groupedProductId)?.name ??
								`${messages.placeholder} ${messages.woodType.toLowerCase()}...`}
						</span>
						<FontAwesomeIcon icon={faChevronDown} fixedWidth className="ml-2" />
					</DropdownMenu.Trigger>

					{hasError && (
						<FormErrorMessage variant="danger">
							<ErrorMessage name={name} />
						</FormErrorMessage>
					)}
				</div>

				<DropdownMenu.Portal>
					<DropdownMenu.Content align="start">
						<div className="mt-4 flex w-[min(calc(100dvw-2rem),450px)] max-w-[calc(100dvw-2rem)] flex-col gap-4 rounded border border-shades bg-white p-4 shadow">
							{grouped.length > 5 && (
								<NativeInput
									autoFocus
									placeholder={`${messages.search} ${messages.woodType.toLowerCase()}...`}
									type="text"
									value={search}
									onChange={(e) => setSearch(e.target.value)}
									onKeyDown={(e) => e.stopPropagation()}
								/>
							)}

							<div className="scrollbar-none flex max-h-80 flex-col gap-2 overflow-scroll">
								{filteredGrouped.length === 0 ? (
									<DropdownMenu.Item>{messages.empty}</DropdownMenu.Item>
								) : (
									filteredGrouped.map((item) => (
										<DropdownMenu.Item
											key={item.id}
											onSelect={() => setValue(name, item.id, { shouldValidate: true })}
											className="cursor-pointer outline-none hover:underline focus-visible:underline"
										>
											<span dangerouslySetInnerHTML={{ __html: item.label }} />
										</DropdownMenu.Item>
									))
								)}
							</div>
						</div>
					</DropdownMenu.Content>
				</DropdownMenu.Portal>
			</DropdownMenu.Root>
		</>
	);
}

interface SimpleProductDropDownProps {
	apiUrl: string;
	lang: string;
	messages: {
		thickness: string;
		placeholder: string;
	};
}

function SimpleProductDropDown(props: SimpleProductDropDownProps) {
	const { apiUrl, lang, messages } = props;

	const name = "simple_product_id";
	const { watch, getFieldState, register, setValue, resetField } =
		useFormContext<ListImportFormType>();
	const groupedProductId = watch("grouped_product_id");
	const simpleProductId = watch(name);
	const hasError = getFieldState(name).invalid;

	const { products, isLoading } = getSimpleProductsData({ apiUrl, groupedProductId });

	if (isLoading) {
		return (
			<div className="flex flex-col gap-1">
				<span className="h-6 w-1/2 animate-pulse rounded bg-shades-light" />
				<span className="h-[2.625rem] w-full animate-pulse rounded bg-shades-light" />
			</div>
		);
	}

	return (
		<>
			<input type="hidden" {...register(name)} />
			<RadixSelect
				disabled={!(groupedProductId && products?.length) || products.length === 1}
				value={simpleProductId}
				onValueChange={(newValue) => {
					const newProduct = products?.find((item) => item.id === newValue);

					if (newProduct) {
						const units = newProduct.available_production_units;
						const newUnit = units?.length
							? (units?.find((u) => u === ProductionUnit.CompletePanel) ?? ProductionUnit.M2)
							: null;

						setValue(name, newProduct?.id, { shouldValidate: true });

						if (newUnit) {
							setValue("production_type", newUnit, { shouldValidate: true });
						} else {
							resetField("production_type");
						}
					} else {
						resetField(name);
					}
				}}
				label={messages.thickness}
				placeholder={`${messages.placeholder} ${messages.thickness.toLowerCase()}...`}
				className="col-span-3 grow lg:col-span-2"
				id={name}
				errorComponent={
					hasError && (
						<FormErrorMessage variant="danger">
							<ErrorMessage name={name} />
						</FormErrorMessage>
					)
				}
			>
				{products?.map((product, index) => (
					<RadixSelectItem value={product.id} key={index}>
						{formatNumber({
							lang,
							value: product.thickness,
							unit: "millimeter",
							digits: { min: 0, max: 0 },
						})}
					</RadixSelectItem>
				))}
			</RadixSelect>
		</>
	);
}

interface ProductionTypeDropDownProps {
	apiUrl: string;
	messages: {
		productionType: string;
		placeholder: string;
		unitCompletePanel: string;
		unitM2: string;
	};
}

function ProductionTypeDropDown(props: ProductionTypeDropDownProps) {
	const { apiUrl, messages } = props;

	const name = "production_type";
	const { watch, getFieldState, register, setValue } = useFormContext<ListImportFormType>();
	const groupedProductId = watch("grouped_product_id");
	const simpleProductId = watch("simple_product_id");
	const productionType = watch(name);
	const hasError = getFieldState(name).invalid;

	const { units } = getSimpleProductData({ apiUrl, groupedProductId, simpleProductId });

	return (
		<>
			<input type="hidden" {...register(name)} />
			<RadixSelect
				disabled={
					!(simpleProductId && units?.find((unit) => unit === ProductionUnit.CompletePanel)) ||
					(!units?.find((unit) => unit === ProductionUnit.M2) && Number(units?.length) >= 1)
				}
				value={productionType?.toString()}
				onValueChange={(value) => {
					return setValue(name, value as ProductionUnit, { shouldValidate: true });
				}}
				label={messages.productionType}
				placeholder={`${messages.placeholder} ${messages.productionType.toLowerCase()}...`}
				className="col-span-3 grow lg:col-span-2"
				id={name}
				errorComponent={
					hasError && (
						<FormErrorMessage variant="danger">
							<ErrorMessage name={name} />
						</FormErrorMessage>
					)
				}
			>
				<RadixSelectItem
					value={ProductionUnit.CompletePanel}
					disabled={!units?.find((unit) => unit === ProductionUnit.CompletePanel)}
				>
					{messages.unitCompletePanel}
				</RadixSelectItem>

				<RadixSelectItem
					value={ProductionUnit.M2}
					disabled={
						!units?.find((unit) => unit === ProductionUnit.M2) && Number(units?.length) >= 1
					}
				>
					{messages.unitM2}
				</RadixSelectItem>
			</RadixSelect>
		</>
	);
}

function SimpleProductAgreement({ apiUrl }: { apiUrl: string }) {
	const { watch } = useFormContext<ListImportFormType>();

	const groupedProductId = watch("grouped_product_id");
	const simpleProductId = watch("simple_product_id");

	const { currentProduct } = getSimpleProductData({ apiUrl, groupedProductId, simpleProductId });

	if (!currentProduct?.agreement?.mandatory) return null;

	return (
		<Checkbox checked disabled className="text-shades-dark text-sm">
			{currentProduct.agreement.text}
		</Checkbox>
	);
}
